import { useRecoilValue, useRecoilState } from "recoil";
import { firstNameState, displayRoleState, selectedTeamState } from "../../store/atoms";
import { ChevronDown, ChevronUp } from "lucide-react";
import { useState, useEffect } from "react";
import useLambda from '../hooks/useLambda';
import { awsExports } from '../../aws-exports';
import AWS from 'aws-sdk';

const COLOR_PAIRS = [
  { bgColor: "#FDF7E7", textColor: "#EBAE12" }, // Yellow
  { bgColor: "#E6F7F4", textColor: "#40C5AF" }, // Green
  { bgColor: "#FFEEEE", textColor: "#FF4D4D" }, // Red
  { bgColor: "#F0E7F9", textColor: "#8A4FFF" }, // Purple
  { bgColor: "#E7F0F9", textColor: "#4F8BFF" }, // Blue
];

const WelcomeHeader = () => {
  const firstName = useRecoilValue(firstNameState);
  const displayRole = useRecoilValue(displayRoleState);
  const [selectedTeam, setSelectedTeam] = useRecoilState(selectedTeamState);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [teams, setTeams] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [teamColorMap, setTeamColorMap] = useState({});

  const [tenantMgmtFunctionName] = useState(awsExports.Lambda.userManagement);

  const { configureLambda, decodeToken, getJWT } = useLambda();

  // const getTeamColors = (teamName) => {
  //   if (teamColorMap[teamName]) {
  //     return teamColorMap[teamName];
  //   }

  //   const hash = teamName.split('').reduce((acc, char) => {
  //     return char.charCodeAt(0) + ((acc << 5) - acc);
  //   }, 0);
  //   const colorIndex = Math.abs(hash) % COLOR_PAIRS.length;
    
  //   return COLOR_PAIRS[colorIndex];
  // };


  const getDisplayName = (teamName) => {
    // Hardcoded display names for specific teams
    const displayNameMap = {
      'yardenateam': 'Swalwell for Governor',
      'gallegoteam': 'GallegoForArizona',
      'suozziTeam':'SuozziForCongress',
      'goldmanTeam':'GoldmanForNewYork' ,
      'ilhanomarteam': 'IlhanFor Congress',
      'kennedydemocratsteam':'KennedyDemocrats',
      'mattTeam':'MattForCongress',
      'morelleteam':'MorelleForCongress',
      'smithtestteam':'SmithTestTeam',
      'haleyTeam': 'StevensForSenate ',
      'gomezTeam':'JimmyGomez',
      'figuresteam':'FiguresForCongress',
      'eswalwellTeam':'Swalwell for Governor',
      'crockettTeam': 'JasmineCrockett',
      'annieTeam':'AnnieAndrewsForSenate'
    };
    return displayNameMap[teamName] || teamName;
  };


  let colorCounter = 0;

  const getTeamColors = (teamName) => {
    if (teamColorMap[teamName]) {
      return teamColorMap[teamName];
    }
    
    const colorIndex = colorCounter % COLOR_PAIRS.length;
    colorCounter++;
    return COLOR_PAIRS[colorIndex];
  };

   // Modify the formattedTeams mapping in the useEffect to include displayName
   useEffect(() => {
    const fetchUserData = async () => {
      try {
        const jwtToken = await getJWT();
        await configureLambda(jwtToken);
        const lambda = new AWS.Lambda();
        
        const payload = {
          jwtToken,
          operation: 'getRequestedTokenFields',
          requested_items: ['cognito:groups'],
        };
            
        const stringifiedPayload = JSON.stringify(payload);
        const params = {
          FunctionName: tenantMgmtFunctionName,
          Payload: stringifiedPayload,
        };
            
        try {
          const result = await lambda.invoke(params).promise();
          const data = typeof result.Payload === 'string' ? JSON.parse(result.Payload) : result.Payload;
          const bodyData = typeof data.body === 'string' ? JSON.parse(data.body) : data.body;
          // console.log('bodyData is : '+JSON.stringify(bodyData));

          const groups = bodyData['cognito:groups'] || [];
          const filteredGroups = groups.includes("Tenants") 
            ? groups.filter(group => group !== 'Tenants') 
            : [...groups];

          const newTeamColorMap = {};
          const formattedTeams = filteredGroups.map(group => {
            const colors = getTeamColors(group);
            newTeamColorMap[group] = colors;
            
            return {
              initials: getInitials(group),
              name: group,
              displayName: getDisplayName(group), // Add displayName here
              ...colors
            };
          });

          setTeamColorMap(newTeamColorMap);
          setTeams(formattedTeams);
          
          if (selectedTeam && formattedTeams.some(t => t.name === selectedTeam.name)) {
            return;
          }
          
          if (formattedTeams.length > 0) {
            setSelectedTeam(formattedTeams[0]);
          }
        } catch (error) {
          console.error('Error invoking getRequestedTokenFields Lambda:', error);
          throw error;
        }        

      } catch (error) {
        console.error('Failed to fetch user data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchUserData();
  }, []);

  // const getInitials = (teamName) => {
  //   if (!teamName) return '';
  //   const parts = teamName.split(/(?=[A-Z])/);
  //   return parts.length >= 2 
  //     ? `${parts[0].charAt(0)}${parts[1].charAt(0)}`.toUpperCase()
  //     : teamName.substring(0, 2).toUpperCase();
  // };

  const getInitials = (teamName) => {
    if (!teamName) return 'NT'; // Default for no team
    return `${teamName.charAt(0).toUpperCase()}T`; // First letter + 'T'
  };

  const toggleDropdown = () => {
    setIsDropdownOpen(!isDropdownOpen);
  };

  const handleTeamSelect = (team) => {
    setSelectedTeam(team);
    setIsDropdownOpen(false);
  };

  const displayTeam = selectedTeam || 
    (teams.length > 0 ? teams[0] : 
    { initials: 'NA', name: 'No Team', bgColor: '#FDF7E7', textColor: '#EBAE12' });

  if (isLoading) {
    return (
      <header className="flex flex-wrap items-center justify-between self-stretch px-10 py-5 border-b border-solid border-b-zinc-300 max-md:px-5">
        <div className="flex flex-wrap gap-2.5 items-end">
          <h1 className="text-xl leading-6 text-black">
            <span className="text-base font-inter leading-5">Welcome,</span>
            <br />
            <span className="font-inter font-bold">{firstName}{firstName && '!'}</span>
          </h1>
          {displayRole && (
            <span className="overflow-hidden gap-2.5 px-1.5 py-1 text-[13px] font-inter font-bold leading-tight text-emerald-400 bg-emerald-50 rounded-lg border border-emerald-100 border-solid">
              {displayRole}
            </span>
          )}
        </div>
        <div className="w-[66px] h-[42px] rounded-[24px] bg-gray-200 animate-pulse"></div>
      </header>
    );
  }

  return (
    <header className="flex flex-wrap items-center justify-between self-stretch px-10 py-5 border-b border-solid border-b-zinc-300 max-md:px-5">
      <div className="flex flex-wrap gap-2.5 items-end">
        <h1 className="text-xl leading-6 text-black">
          <span className="text-base font-inter leading-5">Welcome,</span>
          <br />
          <span className="font-inter font-bold">{firstName}{firstName && '!'}</span>
        </h1>
        {displayRole && (
          <span className="overflow-hidden gap-2.5 px-1.5 py-1 text-[13px] font-inter font-bold leading-tight text-emerald-400 bg-emerald-50 rounded-lg border border-emerald-100 border-solid">
            {displayRole}
          </span>
        )}
      </div>

      <div className="relative">
        <button
          onClick={toggleDropdown}
          className="flex items-center justify-center gap-1.5 w-[66px] h-[42px] px-2.5 rounded-[24px] border"
          style={{
            borderColor: displayTeam.textColor,
            backgroundColor: displayTeam.bgColor,
          }}
        >
          <span 
            className="text-[16px] font-inter font-bold leading-[120%]"
            style={{ color: displayTeam.textColor }}
          >
            {displayTeam.initials}
          </span>
          {isDropdownOpen ? (
            <ChevronUp className="w-4 h-4" style={{ color: displayTeam.textColor }} />
          ) : (
            <ChevronDown className="w-4 h-4" style={{ color: displayTeam.textColor }} />
          )}
        </button>

        {isDropdownOpen && (
    <div className="absolute right-0 mt-2 w-[262px] p-1 flex flex-col items-start rounded-[14px] border border-[#F2F2F2] bg-white shadow-[0px_4px_16px_0px_rgba(0,0,0,0.08)] z-30">
      <div className={`w-full ${teams.length > 3 ? 'max-h-[200px] overflow-y-auto' : ''}`}>
        {teams.map((team) => (
          <div
            key={team.name}
            onClick={() => handleTeamSelect(team)}
            className={`flex p-2 items-center gap-2 w-full rounded-[7px] cursor-pointer ${
              displayTeam.name === team.name ? 'bg-[#F2F2F2]' : 'hover:bg-[#F2F2F2]'
            }`}
          >
            <div 
              className="flex w-9 h-9 p-2.5 justify-center items-center rounded-[19px] aspect-square"
              style={{ backgroundColor: team.bgColor }}
            >
              <span 
                className="text-[16px] font-inter font-bold leading-[120%]"
                style={{ color: team.textColor }}
              >
                {team.initials}
              </span>
            </div>
            <span className="flex-1 text-[16px] font-inter font-bold leading-[120%] text-[#000]">
              {team.displayName} {/* Changed from team.name to team.displayName */}
            </span>
            {displayTeam.name === team.name && (
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path d="M20 6L9 17L4 12" stroke="#40C5AF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            )}
          </div>
        ))}
      </div>
    </div>
  )}
      </div>
    </header>
  );
};

export default WelcomeHeader;